import React, { useEffect, useRef, useState } from "react";
import { Search, Briefcase, Users, FileText, ClipboardSignature, Building2, FolderOpen, CalendarCheck, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const GROUPS = [
  { key: "projects", label: "Projects", icon: Briefcase, link: (r) => `/projects/${r.id}`, text: (r) => `${r.project_name} — ${r.client_name || ""}`, sub: (r) => r.project_id_external || r.service_type },
  { key: "leads", label: "Leads", icon: Users, link: () => "/leads", text: (r) => r.company_name, sub: (r) => `${r.lead_number || ""} · ${r.contact_person || ""}` },
  { key: "proposals", label: "Proposals", icon: FileText, link: () => "/proposals", text: (r) => `${r.proposal_number} — ${r.client_name || ""}`, sub: (r) => r.status },
  { key: "work_orders", label: "Work Orders", icon: ClipboardSignature, link: () => "/work-orders", text: (r) => `${r.wo_number} — ${r.client_name || ""}`, sub: (r) => r.status },
  { key: "clients", label: "Clients", icon: Building2, link: () => "/clients", text: (r) => r.name, sub: (r) => r.email },
  { key: "documents", label: "Documents", icon: FolderOpen, link: (r) => r.project_id ? `/projects/${r.project_id}` : "/repository", text: (r) => r.name, sub: (r) => r.status },
  { key: "compliance", label: "Compliance", icon: CalendarCheck, link: (r) => r.project_id ? `/projects/${r.project_id}` : "/compliance", text: (r) => `${r.type}: ${r.title}`, sub: (r) => r.due_date ? `Due ${r.due_date.slice(0, 10)}` : "" },
];

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults(null); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/search", { params: { q: val.trim() } });
        setResults(data);
        setOpen(true);
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 350);
  };

  const total = results ? Object.values(results).reduce((a, v) => a + (v?.length || 0), 0) : 0;

  return (
    <div className="flex-1 max-w-xl relative" ref={ref}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
      {busy && <Loader2 className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#72BE44]" />}
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (results) setOpen(true); }}
        placeholder="Search clients, projects, EC / consent numbers, documents..."
        className="w-full pl-10 pr-9 py-2 text-sm bg-stone-100/70 border border-transparent rounded-lg focus:outline-none focus:bg-white focus:border-[#72BE44] transition"
      />
      {open && results && (
        <div className="absolute left-0 right-0 mt-2 bg-white border border-stone-200 rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {total === 0 && <div className="px-4 py-6 text-center text-sm text-stone-400">No results for "{q}"</div>}
          {GROUPS.map((g) => {
            const rows = results[g.key] || [];
            if (!rows.length) return null;
            const Icon = g.icon;
            return (
              <div key={g.key}>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-bold text-stone-400">{g.label}</div>
                {rows.map((r) => (
                  <button key={r.id} data-testid={`search-result-${g.key}-${r.id}`}
                    onClick={() => { setOpen(false); setQ(""); setResults(null); nav(g.link(r)); }}
                    className="w-full text-left px-4 py-2 hover:bg-[#72BE44]/10 flex items-center gap-3 transition">
                    <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: "#145353" }} />
                    <div className="min-w-0">
                      <div className="text-sm text-stone-800 truncate">{g.text(r)}</div>
                      {g.sub(r) && <div className="text-[11px] text-stone-400 truncate">{g.sub(r)}</div>}
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
